import { createTheme, ThemeOptions } from '@mui/material/styles';

// Inspired by Stitch design concept - dark theme with vibrant accent
const getDesignTokens = (mode: 'light' | 'dark'): ThemeOptions => ({
    palette: {
        mode,
        ...(mode === 'dark'
            ? {
                // Dark theme colors
                primary: {
                    main: '#46ec13',
                    light: '#6fff3a',
                    dark: '#32b809',
                    contrastText: '#0a1f02',
                },
                secondary: {
                    main: '#a78bfa',
                    light: '#c4b5fd',
                    dark: '#7c3aed',
                    contrastText: '#ffffff',
                },
                background: {
                    default: '#0f172a',
                    paper: '#1e293b',
                },
                text: {
                    primary: '#f1f5f9',
                    secondary: '#cbd5e1',
                },
                divider: 'rgba(148, 163, 184, 0.12)',
                error: {
                    main: '#f87171',
                    light: '#fca5a5',
                    dark: '#dc2626',
                },
                warning: {
                    main: '#fbbf24',
                    light: '#fcd34d',
                    dark: '#f59e0b',
                },
                success: {
                    main: '#34d399',
                    light: '#6ee7b7',
                    dark: '#10b981',
                },
                info: {
                    main: '#60a5fa',
                    light: '#93c5fd',
                    dark: '#3b82f6',
                },
            }
            : {
                // Light theme colors
                primary: {
                    main: '#32b809',
                    light: '#46ec13',
                    dark: '#258007',
                    contrastText: '#ffffff',
                },
                secondary: {
                    main: '#7c3aed',
                    light: '#a78bfa',
                    dark: '#6d28d9',
                    contrastText: '#ffffff',
                },
                background: {
                    default: '#f8fafc',
                    paper: '#ffffff',
                },
                text: {
                    primary: '#0f172a',
                    secondary: '#475569',
                },
                divider: 'rgba(15, 23, 42, 0.12)',
            }),
    },
    typography: {
        fontFamily: '"Manrope", "Google Sans", "Inter", system-ui, -apple-system, sans-serif',
        h1: {
            fontWeight: 800,
            fontSize: '2.5rem',
            lineHeight: 1.2,
            letterSpacing: '-0.02em',
        },
        h2: {
            fontWeight: 700,
            fontSize: '2rem',
            lineHeight: 1.3,
            letterSpacing: '-0.01em',
        },
        h3: {
            fontWeight: 700,
            fontSize: '1.75rem',
            lineHeight: 1.3,
        },
        h4: {
            fontWeight: 600,
            fontSize: '1.5rem',
            lineHeight: 1.4,
        },
        h5: {
            fontWeight: 600,
            fontSize: '1.25rem',
            lineHeight: 1.4,
        },
        h6: {
            fontWeight: 600,
            fontSize: '1.125rem',
            lineHeight: 1.5,
        },
        subtitle1: {
            fontSize: '1rem',
            lineHeight: 1.5,
            fontWeight: 500,
        },
        subtitle2: {
            fontSize: '0.875rem',
            lineHeight: 1.5,
            fontWeight: 500,
        },
        body1: {
            fontSize: '1rem',
            lineHeight: 1.6,
        },
        body2: {
            fontSize: '0.875rem',
            lineHeight: 1.6,
        },
        button: {
            fontWeight: 600,
            textTransform: 'none',
            letterSpacing: '0.01em',
        },
    },
    shape: {
        borderRadius: 12, // Rounded design
    },
    spacing: 8, // 8px base unit
    shadows: mode === 'dark'
        ? [
            'none',
            '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
            '0 1px 3px 0 rgba(0, 0, 0, 0.4), 0 1px 2px 0 rgba(0, 0, 0, 0.3)',
            '0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.3)',
            '0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.3)',
            '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.3)',
            '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            'none', 'none', 'none', 'none', 'none', 'none', 'none',
            'none', 'none', 'none', 'none', 'none', 'none', 'none',
            'none', 'none', 'none', 'none',
        ]
        : [
            'none',
            '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            'none', 'none', 'none', 'none', 'none', 'none', 'none',
            'none', 'none', 'none', 'none', 'none', 'none', 'none',
            'none', 'none', 'none', 'none',
        ],
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    padding: '8px 16px',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        transform: 'translateY(-1px)',
                    },
                },
                contained: {
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    '&:hover': {
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2)',
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    transition: 'all 0.2s ease-in-out',
                    backdropFilter: mode === 'dark' ? 'blur(10px)' : 'none',
                    backgroundColor: mode === 'dark' ? 'rgba(30, 41, 59, 0.8)' : '#ffffff',
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                },
                elevation1: {
                    boxShadow: mode === 'dark'
                        ? '0 1px 3px 0 rgba(0, 0, 0, 0.4)'
                        : '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backdropFilter: 'blur(10px)',
                    backgroundColor: mode === 'dark'
                        ? 'rgba(15, 23, 42, 0.8)'
                        : 'rgba(255, 255, 255, 0.8)',
                    boxShadow: mode === 'dark'
                        ? '0 1px 3px 0 rgba(0, 0, 0, 0.4)'
                        : '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    borderRadius: 0,
                    backdropFilter: mode === 'dark' ? 'blur(10px)' : 'none',
                    backgroundColor: mode === 'dark'
                        ? 'rgba(30, 41, 59, 0.95)'
                        : '#ffffff',
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    fontWeight: 500,
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 8,
                    },
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: 16,
                    backdropFilter: mode === 'dark' ? 'blur(10px)' : 'none',
                    backgroundColor: mode === 'dark'
                        ? 'rgba(30, 41, 59, 0.95)'
                        : '#ffffff',
                },
            },
        },
        MuiListItem: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    transition: 'all 0.15s ease-in-out',
                },
            },
        },
    },
});

export const createAppTheme = (mode: 'light' | 'dark') => {
    return createTheme(getDesignTokens(mode));
};

export default createAppTheme;
